/**
 * Phase 7B — Budget Increase approval sheet (per-request).
 *
 * Borrows the visual language of Phase 3's projectApprovalBottomSheet.tsx —
 * 3-stage stepper (BU Head -> Dept Head -> Finance), action row with Reject /
 * Approve / Approve-with-changes (amount editable at Dept Head + Finance
 * stages). The Finance tier re-anchors server-side if Dept Head reduces the
 * amount below a tier threshold.
 */

import { FunctionComponent, useEffect, useState } from "react";
import StepperWidget from "../../../../ui/new_components/common/stepper_widget";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import { ACTION } from "../../../../utils/enums";
import { AdditionalFundRequest } from "../../models/cost_center_model";
import {
  project_approval_level,
  project_approval_threshold,
  project_levelModule,
  ProjectApprovalStatus,
} from "../../models/project_model";

interface Props {
  cc_id: string;
  cc_name: string;
  request: AdditionalFundRequest;
  onSubmit: (payload: {
    request_id: string;
    level: string;
    approval_status: string;
    modified_amount?: number;
    reason?: string;
  }) => void;
}

const CCBudgetRequestApprovalSheet: FunctionComponent<Props> = ({
  request,
  onSubmit,
}) => {
  const { isAuthorised } = useAuth();
  const [canActAtCurrent, setCanActAtCurrent] = useState(false);
  const [modifiedAmount, setModifiedAmount] = useState<number | "">("");

  useEffect(() => {
    const currentModule =
      project_levelModule[request.current_approval_level || ""];
    setCanActAtCurrent(
      !!currentModule &&
        isAuthorised({ module: currentModule, action: ACTION.UPDATE }),
    );
    setModifiedAmount("");
  }, [request.current_approval_level, isAuthorised]);

  const rule = project_approval_threshold[request.approval_level || ""];
  const steps = rule ? rule.approvals_required.map((r) => r.level) : [];
  const activeIdx = steps.indexOf(request.current_approval_level || "");
  const isFinalised =
    request.approvalStatus === ProjectApprovalStatus.approved ||
    request.approvalStatus === ProjectApprovalStatus.rejected;

  const currentLevel = request.current_approval_level || "";
  const canModifyAmount =
    currentLevel === project_approval_level.dept_head ||
    currentLevel === project_approval_level.finance_L1 ||
    currentLevel === project_approval_level.finance_L2 ||
    currentLevel === project_approval_level.finance_L3;

  if (!rule) return null;

  return (
    <div className="mt-2 border rounded-md p-3 bg-gray-50">
      <div className="pb-4">
        <StepperWidget
          steps={steps.map((label, i) => ({
            label,
            validated:
              i < activeIdx ||
              (isFinalised &&
                request.approvalStatus === ProjectApprovalStatus.approved),
            required: false,
          }))}
          activeStep={activeIdx < 0 ? 0 : activeIdx}
          onChange={() => {}}
        />
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="text-xs text-gray-500 min-w-0">
          <div>
            Request <span className="font-mono">{request.request_id}</span> ·
            requested by <b>{request.requested_by_name}</b>
          </div>
          <div className="mt-0.5">
            Current amount:{" "}
            <b>₹{Number(request.current_amount).toLocaleString("en-IN")}</b>
            {request.requested_amount !== request.current_amount && (
              <span className="ml-2 text-amber-700">
                (originally ₹
                {Number(request.requested_amount).toLocaleString("en-IN")})
              </span>
            )}
            {" · "}Tier <b>{request.approval_level}</b>
            {" · "}Status <b>{request.approvalStatus}</b>
          </div>
          {request.reason && (
            <div className="mt-0.5 text-gray-600 italic truncate">
              "{request.reason}"
            </div>
          )}
        </div>

        {canActAtCurrent && !isFinalised && (
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            {canModifyAmount && (
              <div className="flex flex-col">
                <label className="text-[10px] uppercase tracking-wide text-gray-500">
                  Modify amount (optional)
                </label>
                <input
                  type="number"
                  data-testid={`afr-modify-${request.request_id}`}
                  placeholder={String(request.current_amount)}
                  value={modifiedAmount}
                  onChange={(e) =>
                    setModifiedAmount(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="border border-gray-300 rounded-md px-2 py-1 text-xs w-40"
                />
              </div>
            )}
            <button
              data-testid={`afr-reject-${request.request_id}`}
              onClick={() => {
                const reason =
                  window.prompt("Reason for rejection?")?.trim() || "";
                if (!reason) return;
                if (!window.confirm(`Reject budget request at ${currentLevel}?`))
                  return;
                onSubmit({
                  request_id: request.request_id,
                  level: currentLevel,
                  approval_status: ProjectApprovalStatus.rejected,
                  reason,
                });
              }}
              className="rounded-md border border-myPrimaryColor px-3 py-1.5 text-myPrimaryColor text-sm"
            >
              Reject
            </button>
            <button
              data-testid={`afr-approve-${request.request_id}`}
              onClick={() => {
                const payload: {
                  request_id: string;
                  level: string;
                  approval_status: string;
                  modified_amount?: number;
                } = {
                  request_id: request.request_id,
                  level: currentLevel,
                  approval_status: ProjectApprovalStatus.approved,
                };
                if (
                  canModifyAmount &&
                  modifiedAmount !== "" &&
                  Number(modifiedAmount) !== request.current_amount
                ) {
                  payload.modified_amount = Number(modifiedAmount);
                }
                const confirmMsg = payload.modified_amount
                  ? `Approve at ${currentLevel} with amount changed to ₹${payload.modified_amount.toLocaleString("en-IN")}?`
                  : `Approve at ${currentLevel}?`;
                if (!window.confirm(confirmMsg)) return;
                onSubmit(payload);
              }}
              className="bg-myPrimaryColor border border-myPrimaryColor rounded-md px-3 py-1.5 text-white text-sm"
            >
              {modifiedAmount !== "" &&
              canModifyAmount &&
              Number(modifiedAmount) !== request.current_amount
                ? "Approve with changes"
                : "Approve"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CCBudgetRequestApprovalSheet;
